import { useCallback, useEffect, useRef, useState } from 'react';
import {
  HeroPointInsufficientBalanceError,
  HeroPointServiceModule,
} from '../services/host/HeroPointServiceModule';

/**
 * A6e (2026-06-03) — Server-backed Hero Point ledger hook.
 *
 * Replaces the A6 local-state shim. On mount, the hook fetches the
 * current balance from /api/modules/briefing-room/campaigns/{cid}/hero-points.
 * If 404 (uninitialized), the hook POSTs `/initialize` with `initialPoints`.
 * `spend()` performs an optimistic decrement, POSTs `/spend`, and reverts
 * on failure.
 *
 * Per CLAUDE.md memory rule `feedback_async_then_render_needs_loading_state`:
 * the hook exposes three states (idle → loading → active) via the new
 * `isLoading` field. The return type also adds `error` for the
 * revert-on-failure path; the ScenarioRunner caller consumes it via
 * console.warn (no toast UI ships in A6e).
 *
 * Contract preserved from the A6 shim:
 *   - `{heroPoints, spend, canSpend}` stay (ScenarioRunner doesn't break)
 *   - `spend()` at HP=0 is still a no-op (server is not called)
 *   - `onSpend` callback still fires via queueMicrotask after a SUCCESSFUL
 *     spend (the optimistic decrement is what users see; the post-server-
 *     confirmation timing matches the original microtask contract).
 *
 * New fields:
 *   - `isLoading`: true during the initial GET / POST initialize
 *   - `error`: string | null — set when a server call fails
 *   - `spend(reason?: string)`: reason for the ledger entry (defaults to 'spend')
 */
export interface UseHeroPointSpendOptions {
  campaignId: string;
  initialPoints: number;
  onSpend?: () => void;
  /**
   * Factory injected for tests. Defaults to constructing a real
   * HeroPointServiceModule (which uses real fetch + localStorage auth).
   */
  serviceFactory?: () => HeroPointServiceModule;
}

export interface UseHeroPointSpendResult {
  heroPoints: number;
  spend: (reason?: string) => void;
  canSpend: boolean;
  isLoading: boolean;
  error: string | null;
}

const DEFAULT_SPEND_REASON = 'spend';

function clampToNonNegInt(value: number): number {
  return Math.max(0, Math.floor(value));
}

export function useHeroPointSpend({
  campaignId,
  initialPoints,
  onSpend,
  serviceFactory,
}: UseHeroPointSpendOptions): UseHeroPointSpendResult {
  // Optimistic / authoritative balance. Initialized from initialPoints so
  // the UI has a defensible value before the first round-trip completes.
  const [heroPoints, setHeroPoints] = useState<number>(
    clampToNonNegInt(initialPoints),
  );
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);

  // Mirror the latest balance into a ref so spend() can read it
  // synchronously without waiting for React's setState callback to fire
  // (the setState updater callback runs asynchronously in React 18, so a
  // mutable-closure flag set inside it is unreliable for guarding the
  // server call).
  const heroPointsRef = useRef<number>(clampToNonNegInt(initialPoints));
  heroPointsRef.current = heroPoints;

  // The service instance is stable across renders so tests can spy on
  // method calls. Created lazily via the factory.
  const serviceRef = useRef<HeroPointServiceModule | null>(null);
  if (serviceRef.current === null) {
    serviceRef.current = serviceFactory
      ? serviceFactory()
      : new HeroPointServiceModule();
  }

  // Refs for the latest values so the spend callback doesn't need to
  // re-bind on every state change.
  const onSpendRef = useRef<(() => void) | undefined>(onSpend);
  onSpendRef.current = onSpend;

  // Initial sync: fetch existing state OR initialize.
  useEffect(() => {
    let cancelled = false;
    setIsLoading(true);
    setError(null);
    const service = serviceRef.current;
    if (service === null) {
      setIsLoading(false);
      return () => {
        cancelled = true;
      };
    }

    void (async () => {
      try {
        // 2026-06-08: POST /initialize directly on mount. The backend route
        // is idempotent — if the campaign already has a hero_point_balance
        // row, it returns that existing state; otherwise it inserts a new
        // singleton with `initial = initialPoints`. Skipping the prior
        // GET-then-404-then-POST eliminates a 404 in DevTools console on
        // every fresh campaign load (Finding #1 from VG35014 MCP playthrough
        // 2026-06-08). The single round-trip is also faster than the
        // two-round-trip path on cold campaigns.
        const state = await service.initialize(
          campaignId,
          clampToNonNegInt(initialPoints),
        );
        if (cancelled) return;
        setHeroPoints(state.balance);
        setIsLoading(false);
      } catch (exc) {
        if (cancelled) return;
        // Graceful fallback: keep clamped initialPoints as the local
        // value so the UI continues. Surface the error for the caller.
        const message = exc instanceof Error ? exc.message : String(exc);
        setError(message);
        setHeroPoints(clampToNonNegInt(initialPoints));
        setIsLoading(false);
      }
    })();

    return () => {
      cancelled = true;
    };
    // We intentionally re-sync when campaignId changes, but NOT when
    // initialPoints changes (it's the seed, not the source-of-truth).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [campaignId]);

  const spend = useCallback(
    (reason: string = DEFAULT_SPEND_REASON) => {
      // Read the latest balance synchronously via the ref so we can
      // guard idempotency at HP=0 without relying on React's async
      // setState updater callback. (Setting a flag inside setState's
      // updater and checking it on the next line is a race in React 18.)
      if (heroPointsRef.current <= 0) {
        return;
      }
      const service = serviceRef.current;
      if (service === null) {
        return;
      }
      // Optimistic decrement.
      setHeroPoints((current) => Math.max(0, current - 1));
      heroPointsRef.current = Math.max(0, heroPointsRef.current - 1);
      // Fire the post-decrement callback via microtask so the new
      // heroPoints value commits before the caller observes onSpend.
      // Preserves the original A6 timing contract.
      if (onSpendRef.current) {
        queueMicrotask(onSpendRef.current);
      }
      void (async () => {
        try {
          const result = await service.spend(campaignId, 1, reason);
          // Server-authoritative balance — keep optimistic value if
          // they agree; on disagreement (e.g., concurrent award), the
          // server wins.
          setHeroPoints(result.balance);
          heroPointsRef.current = result.balance;
          setError(null);
        } catch (exc) {
          // Revert optimistic decrement on failure.
          setHeroPoints((current) => current + 1);
          heroPointsRef.current = heroPointsRef.current + 1;
          const message =
            exc instanceof HeroPointInsufficientBalanceError
              ? exc.message
              : exc instanceof Error
                ? exc.message
                : String(exc);
          setError(message);
        }
      })();
    },
    [campaignId],
  );

  return {
    heroPoints,
    spend,
    canSpend: heroPoints > 0,
    isLoading,
    error,
  };
}
